import type { HTMLAttributes, ReactNode } from "react";
import styles from "./states.module.css";
import { Button } from "./Button.js";
import { cx } from "./cx.js";

/**
 * Loading, empty, error and partial. Built together rather than happy-path
 * first, because the fourth one is what separates a trustworthy money app from
 * an untrustworthy one.
 */

interface SkeletonProps extends HTMLAttributes<HTMLSpanElement> {
  /** Any CSS length. Skeletons are shaped like the content they replace. */
  width?: string;
  height?: string;
  /** Sized to a line box so resolving does not shift the layout. */
  text?: boolean;
}

export function Skeleton({
  width,
  height,
  text = false,
  className,
  style,
  ...rest
}: SkeletonProps) {
  return (
    <span
      className={cx(styles.skeleton, text && styles.skeletonText, className)}
      style={{ display: "block", width, height, ...style }}
      aria-hidden="true"
      {...rest}
    />
  );
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <span role="status" aria-label={label}>
      <span className={styles.spinner} aria-hidden="true" />
    </span>
  );
}

interface StateProps {
  title: string;
  /** What to do next. Never "No data available". */
  body?: ReactNode;
  action?: ReactNode;
}

export function EmptyState({ title, body, action }: StateProps) {
  return (
    <div className={styles.state}>
      <p className={styles.stateTitle}>{title}</p>
      {body && <p className={styles.stateBody}>{body}</p>}
      {action}
    </div>
  );
}

interface ErrorStateProps extends StateProps {
  /** A retry that actually retries. */
  onRetry?: () => void;
}

export function ErrorState({
  title = "That did not load",
  body,
  action,
  onRetry,
}: Partial<ErrorStateProps>) {
  return (
    <div className={styles.state} role="alert">
      <p className={styles.stateTitle}>{title}</p>
      {body && <p className={styles.stateBody}>{body}</p>}
      {onRetry && (
        <Button variant="secondary" onClick={onRetry}>
          Try again
        </Button>
      )}
      {action}
    </div>
  );
}

interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  tone?: "info" | "positive" | "warning" | "danger";
  children?: ReactNode;
}

const ALERT_CLASS = {
  info: styles.alertInfo,
  positive: styles.alertPositive,
  warning: styles.alertWarning,
  danger: styles.alertDanger,
} as const;

export function Alert({ tone = "info", className, children, ...rest }: AlertProps) {
  return (
    <div
      className={cx(styles.alert, ALERT_CLASS[tone], className)}
      role={tone === "danger" ? "alert" : "status"}
      {...rest}
    >
      {children}
    </div>
  );
}

interface StatProps {
  label: ReactNode;
  /** Usually a <Money>. */
  value: ReactNode;
  meta?: ReactNode;
  /**
   * The partial state. When a figure is computed from incomplete data — an
   * account whose consent expired, a sync that failed — say so on the number
   * itself rather than reporting an incomplete total as though it were whole.
   */
  incomplete?: ReactNode;
}

export function Stat({ label, value, meta, incomplete }: StatProps) {
  return (
    <div className={styles.stat}>
      <span className={styles.statLabel}>{label}</span>
      <span className={styles.statValue}>{value}</span>
      {incomplete ? (
        <span className={cx(styles.statMeta, styles.statStale)}>{incomplete}</span>
      ) : (
        meta && <span className={styles.statMeta}>{meta}</span>
      )}
    </div>
  );
}
